'use client';
import React, { useState, useEffect, useRef } from 'react';
import CollapsePanel from '../collapsePanel';
import UKPageError from '@/components/UKPageError';
import UKPageNothing from '@/components/UKPageNothing';
import { fetchQuestionDetail } from '@/apis/help/help.api';
import HistoryIcon from '@/assets/icons/HistoryIcon.svg';
import RichTextLoading from './RichTextLoading';
import styles from './index.module.scss';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/i18n/client';
import { HelpDetailWrapper } from '@/styles/product/index';

interface MenuItem {
  urlName: string;
  title: string;
}

interface IDetailProps {
  menuList: MenuItem[];
  detailContent: string;
  detailTitle: string;
  id?: string;
  detailScene: string;
}

const Detail: React.FC<IDetailProps> = ({
  menuList,
  detailContent,
  detailTitle,
  id,
  detailScene,
}) => {
  const [title, setTitle] = useState<string>(detailTitle);
  const [detail, setDetail] = useState<string>(detailContent);
  const [scene, setScene] = useState<string>(detailScene);
  const [isPageErr, setIsPageErr] = useState<boolean>(false); // 是否页面异常
  const [detailLoading, setDetailLoading] = useState<boolean>(false); // 详情加载状态
  const menuRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const route = useRouter();
  const { t } = useTranslation();

  useEffect(() => {
    const menu = menuRef.current;
    const content = contentRef.current;
    if (menu && content) {
      const menuHeight = menu.offsetHeight;
      content.style.minHeight = `${menuHeight}px`; // 设置高度
    }
  }, [detail]);

  // 获取问题详情
  const getDetail = async (id: string) => {
    setDetailLoading(true);
    setIsPageErr(false);
    try {
      const { data, code } = await fetchQuestionDetail({ scene: id });
      setDetailLoading(false);
      if (code === '200') {
        setDetail(data.facadeContent);
        setTitle(data.facadeName);
        setScene(data.scene);
      }
    } catch (error) {
      setIsPageErr(true);
    }
  };

  const handleTryAgain = () => {
    if (id) {
      return getDetail(id);
    }
    return null;
  };

  const handleToVersion = () => {
    route.push('/privacy-policy/archive');
  };

  return (
    <div className={styles.detailWrapper} id='white_screen_detection_element'>
      {isPageErr ? (
        <UKPageError btnText={t('common.btnText.tryAgain')} clickTryAgain={handleTryAgain} />
      ) : (
        <div className={styles.mainWrapper}>
          {id && !!menuList.length && (
            <div ref={menuRef} className={styles.menuWrapper}>
              <CollapsePanel items={menuList} detailId={id} getDetail={getDetail} />
            </div>
          )}
          {detailLoading ? (
            <div ref={contentRef} className={styles.itemContent}>
              <RichTextLoading />
            </div>
          ) : detail ? (
            <div ref={contentRef} className={styles.itemContent}>
              <div className={styles.contentTitle}>
                {title}
                {scene === 'privacy-policy' && (
                  <div className={styles.versionIconWrap} onClick={handleToVersion}>
                    <HistoryIcon />
                    <span>{t('privacyPolicy.archivedVersion')}</span>
                  </div>
                )}
              </div>
              <HelpDetailWrapper dangerouslySetInnerHTML={{ __html: detail }} />
            </div>
          ) : (
            <div className={styles['empty-wrapper']}>
              <UKPageNothing text={t('common.nothingMore')} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Detail;
